// The version navigator in the composer-level control bar, beside undo/redo. Calm at rest —
// a compact "‹ ●94 · v4/6 ›" stepper. The arrows preview the prev/next version live on the
// canvas; the label opens the full Versions timeline. Previewing an older version shows a
// Restore / Latest affordance. T3 chrome (it governs the whole composer, not the photo).
// Renders nothing until the post has a version journey.

import { ChevronLeft, ChevronRight, History, RotateCcw, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useComposerEditor } from './CompositionEditorProvider'
import { useCreativeVersions } from './use-versions'
import { VersionsTimeline } from './VersionsTimeline'

const STEP_BTN =
  'text-paper-dim hover:bg-bg-3 hover:text-paper flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-30'

export function VersionStepper() {
  const ctx = useComposerEditor()
  const creativeId = ctx?.creativeId
  const versions = useCreativeVersions(creativeId)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close the timeline on an outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  if (!ctx || versions.length === 0) return null
  const { previewSeq, previewVersion, exitPreview, restoreVersion } = ctx

  const total = versions.length
  const tipSeq = versions[total - 1]!.seq
  const currentSeq = previewSeq ?? tipSeq
  // Clamp: if the previewed seq vanished from a refetch (e.g. pruned), fall back to the tip
  // index so the label never shows v0/n and the arrows stay correctly bounded.
  const rawIdx = versions.findIndex(v => v.seq === currentSeq)
  const currentIdx = rawIdx === -1 ? total - 1 : rawIdx
  const isPreviewing = previewSeq != null

  const goTo = (idx: number) => {
    const v = versions[idx]
    if (!v) return
    if (idx === total - 1) exitPreview()
    else void previewVersion(v.seq)
  }

  return (
    <div ref={wrapRef} className="relative flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => goTo(currentIdx - 1)}
        disabled={currentIdx <= 0}
        title="Previous version"
        className={STEP_BTN}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title="Version history"
        className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-[12px] font-medium text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper ${
          open ? 'bg-bg-3' : ''
        }`}
      >
        <History className="h-3.5 w-3.5 text-paper-faint" />
        <span className="tabular-nums">
          v{currentIdx + 1}/{total}
        </span>
      </button>

      <button
        type="button"
        onClick={() => goTo(currentIdx + 1)}
        disabled={currentIdx >= total - 1}
        title="Next version"
        className={STEP_BTN}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {/* Previewing an older version → restore it or jump back to the live tip. */}
      {isPreviewing && (
        <>
          <span className="mx-0.5 h-4 w-px bg-line" />
          <button
            type="button"
            onClick={() => void restoreVersion(currentSeq)}
            className="text-ink-on-acid flex items-center gap-1 rounded-full bg-acid px-2 py-1 text-[11px] font-medium hover:bg-acid/90"
          >
            <RotateCcw className="h-3 w-3" />
            Restore
          </button>
          <button type="button" onClick={exitPreview} title="Back to latest" className={STEP_BTN}>
            <X className="h-4 w-4" />
          </button>
        </>
      )}

      {open && (
        <VersionsTimeline
          versions={versions}
          currentSeq={currentSeq}
          onPreview={seq => {
            if (seq === tipSeq) exitPreview()
            else void previewVersion(seq)
          }}
          onRestore={seq => {
            setOpen(false)
            void restoreVersion(seq)
          }}
        />
      )}
    </div>
  )
}
